/**
 * Optimized Elfsight Review Clickability System
 * - Makes entire widget clickable
 * - Event delegation for memory efficiency 
 * - Intersection Observer for precise detection
 * - Single function controls all widgets
 */

(function() {
  'use strict';
  
  // Configuration
  const CONFIG = {
    // Widget-specific selectors
    selectors: [
      '[class*="elfsight-app"]',
      '[data-elfsight-app-id]',
      '.elfsight-app-fa5d0d2b-3e3b-452d-9d8a-e2680bd3da97', // Footer widget
      '.elfsight-app-1e1e26a9-8d38-4ae0-82a1-8c6830e66a5a', // Header/stars widget
      '.elfsight-app-5f0f03c6-e9ab-45ca-a466-3a700212d13c'  // Product page widget
    ],
    
    // Redirect URL
    redirectUrl: 'https://www.isell.pt/pages/avaliacoes-comentarios-clientes',
    
    // CSS classes
    clickableClass: 'clickable-widget',
    processedAttribute: 'data-elfsight-processed'
  };

  // Processed elements tracking
  let processedElements = new WeakSet();
  let observer = null;

  /**
   * Make widget clickable with optimized event handling
   */
  function makeWidgetClickable(element) {
    if (processedElements.has(element) || 
        element.hasAttribute(CONFIG.processedAttribute)) {
      return false;
    }

    // Mark as processed
    element.setAttribute(CONFIG.processedAttribute, 'true');
    processedElements.add(element);

    // Add styling classes
    element.style.cursor = 'pointer';
    element.classList.add(CONFIG.clickableClass);

    // No hover effects - keep simple

    return true;
  }

  /**
   * Process widget container to make it clickable
   */
  function processWidget(widgetElement) {
    if (!widgetElement) return 0;

    // Make the entire widget container clickable
    if (makeWidgetClickable(widgetElement)) {
      return 1;
    }

    return 0;
  }

  /**
   * Scan for widgets using optimized selectors
   */
  function scanForWidgets() {
    let totalProcessed = 0;
    
    CONFIG.selectors.forEach(selector => {
      const widgets = document.querySelectorAll(selector);
      widgets.forEach(widget => {
        totalProcessed += processWidget(widget);
      });
    });

    return totalProcessed;
  }

  /**
   * Initialize intersection observer for efficient detection
   */
  function initializeObserver() {
    if (typeof window.IntersectionObserver === 'undefined') {
      // Fallback for browsers without Intersection Observer
      return initializeFallback();
    }

    observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          processWidget(entry.target);
        }
      });
    }, {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    });

    // Observe existing widgets
    CONFIG.selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(widget => {
        observer.observe(widget);
      });
    });
  }

  /**
   * Fallback for browsers without Intersection Observer
   */
  function initializeFallback() {
    // Initial scan
    scanForWidgets();

    // Watch for new widgets with throttled MutationObserver
    if (typeof window.MutationObserver !== 'undefined') {
      let timeout = null;
      
      const mutationObserver = new MutationObserver(() => {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(scanForWidgets, 500);
      });

      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  /**
   * Global click handler using event delegation
   */
  function initializeClickHandler() {
    document.addEventListener('click', function(event) {
      // Debug: Log all clicks on product page widgets
      const productWidget = event.target.closest('.elfsight-app-5f0f03c6-e9ab-45ca-a466-3a700212d13c');
      if (productWidget) {
        console.log('🎯 Click detected on product widget:', event.target);
        console.log('🎯 Widget has clickable class:', productWidget.classList.contains(CONFIG.clickableClass));
        console.log('🎯 Widget has processed attribute:', productWidget.hasAttribute(CONFIG.processedAttribute));
      }
      
      // Check if clicked element is inside a widget first
      let clickedWidget = event.target.closest('.' + CONFIG.clickableClass);
      
      if (!clickedWidget) {
        // Also check for the processed attribute on any parent
        clickedWidget = event.target.closest('[' + CONFIG.processedAttribute + ']');
      }
      
      // Only proceed if we're inside one of our Elfsight widgets
      if (clickedWidget && (
        clickedWidget.closest('.elfsight-app-1e1e26a9-8d38-4ae0-82a1-8c6830e66a5a') || // Header/navbar widget
        clickedWidget.closest('.elfsight-app-fa5d0d2b-3e3b-452d-9d8a-e2680bd3da97') || // Footer widget  
        clickedWidget.closest('.elfsight-app-5f0f03c6-e9ab-45ca-a466-3a700212d13c')    // Product page widget
      )) {
        // Only exclude interactive elements that are INSIDE the Elfsight widget itself
        const interactiveElements = 'form, input, button, textarea, select, a[href], [contenteditable], [tabindex]';
        
        // Check if the actual click target is an interactive element within the widget
        if (event.target.matches(interactiveElements)) {
          // Only prevent redirect if it's a genuine interactive element (like a form button inside the widget)
          const isGenuineInteractive = event.target.tagName === 'A' && event.target.hasAttribute('href') ||
                                      event.target.tagName === 'BUTTON' ||
                                      event.target.tagName === 'INPUT' ||
                                      event.target.tagName === 'TEXTAREA' ||
                                      event.target.tagName === 'SELECT' ||
                                      event.target.hasAttribute('contenteditable');
          
          if (isGenuineInteractive) {
            return; // Allow normal interaction for genuine interactive elements
          }
        }
        
        // Redirect to reviews page
        event.preventDefault();
        event.stopPropagation();
        window.location.href = CONFIG.redirectUrl;
      }
    }, true); // Use capture phase to catch events before other handlers

    // No hover effects - removed per user request
  }

  /**
   * Initialize the system
   */
  function initialize() {
    // Wait for DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialize);
      return;
    }

    // Set up event delegation first
    initializeClickHandler();

    // Initialize widget detection
    if (typeof window.IntersectionObserver !== 'undefined') {
      // Use modern Intersection Observer
      setTimeout(initializeObserver, 100);
    } else {
      // Fallback for older browsers
      setTimeout(initializeFallback, 100);
    }

    // Additional scan after widgets typically load
    setTimeout(scanForWidgets, 2000);
    
    // Extra scan specifically for product page widgets (they load slower)
    setTimeout(() => {
      const productWidget = document.querySelector('.elfsight-app-5f0f03c6-e9ab-45ca-a466-3a700212d13c');
      if (productWidget && !processedElements.has(productWidget)) {
        console.log('🔧 Processing late-loading product widget');
        processWidget(productWidget);
      }
    }, 5000);
  }

  /**
   * Cleanup function
   */
  function cleanup() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    processedElements = new WeakSet();
  }

  // Auto-initialize
  initialize();

  // Expose cleanup for testing
  if (window.eapps && window.eapps.platform) {
    window.eapps.platform.elfsightReviewsCleanup = cleanup;
  }

})();